'use client';

import {
  AttributeSide,
  AttributeSideDynamic,
} from '@eliadmin/core/src/components/categories/attributes/attribute';
import { AttributeCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-category';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

const FilterSeparator = () => <Separator className="w-auto last-of-type:hidden" />;

export function CategoryFilterSheet() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const categoryTree = useCategoryStore(state => state.categoryTree);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();
  const clearFilter = useCategoryStore(s => s.clearFilters);

  const attributes = category.attributes?.filter(
    attribute => attribute.name && attribute.values?.length,
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="flex h-[52px] max-h-[52px] w-full items-center justify-center gap-2 whitespace-nowrap sm:max-h-10 sm:w-fit lg:max-h-10"
        >
          <Image src={'/images/icons/candle-2.svg'} alt={t('text174')} width={20} height={20} />{' '}
          {t('text174')}
        </Button>
      </SheetTrigger>
      <SheetContent
        aria-describedby={undefined}
        overlayClassName="bg-black/30 backdrop-blur-none"
        className="w-full max-w-[444px] border-none p-0 sm:p-0"
        showCloseIcon={false}
      >
        <Card
          className={cn('relative flex h-screen max-h-screen flex-col', 'ea-category-filter-sheet')}
        >
          <div
            className={cn(
              'flex w-full flex-shrink-0 flex-row items-center justify-between px-4 pb-6 pt-8 sm:px-6 sm:pt-6',
              'ea-category-filter-sheet__expand-button',
            )}
          >
            <SheetTitle asChild>
              <h3 className="ea-heading__title h3 font-heading font-medium tracking-tight text-black">
                {t('text174')}
              </h3>
            </SheetTitle>
            <SheetClose aria-label="Zavrieť filter">
              <Image
                src={'/images/icons/close-circle.svg'}
                alt={t('text174')}
                width={24}
                height={24}
                className="object-contain"
              />
            </SheetClose>
          </div>

          <div
            className={cn(
              'flex flex-1 flex-col gap-4 overflow-auto px-4 sm:px-6',
              'ea-category-filter-sheet__content',
            )}
          >
            <FilterSeparator />
            {/* categories */}
            {!!templateSettings.category?.tree_list?.enabled && !!categoryTree && (
              <AttributeCategory order={templateSettings.category.tree_list.order} />
            )}

            <FilterSeparator />

            {/* subcategories */}
            {!!templateSettings.category?.categories_in_filter?.enabled &&
              !!category.sub_categories?.length && (
                <>
                  <AttributeSide
                    name={t('text337')}
                    type="subCategory"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category.categories_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__subcategories') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* manufacturers */}
            {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
              !!category.manufacturers?.length && (
                <>
                  <AttributeSide
                    name={t('text195')}
                    type="generic"
                    attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                    order={templateSettings.category.manufacturers_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* statuses */}
            {!!templateSettings.category?.statuses_in_filter?.enabled &&
              !!category.statuses?.length && (
                <>
                  <AttributeSide
                    name={t('text518')}
                    type="generic"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category?.categories_in_filter?.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* availabilities */}
            {!!templateSettings.category?.availability_in_filter?.enabled &&
              !!category.availabilities?.length && (
                <>
                  <AttributeSide
                    name={t('text339')}
                    type="generic"
                    attribute={{ type: 'availability', type2: 'availabilities' }}
                    order={templateSettings.category.availability_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
                  />
                  <FilterSeparator />
                </>
              )}

            {/* attributes */}
            {!!templateSettings.category?.attributes_in_filter?.enabled && (
              <>
                {attributes?.map((attribute, i) => (
                  <>
                    <AttributeSideDynamic
                      key={`attribute-${attribute.id ?? attribute.name}-${i}`}
                      name={attribute.name}
                      attribute={attribute as any}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__attributes') }}
                    />
                    <FilterSeparator />
                  </>
                ))}
              </>
            )}

            {/* price */}
            {!!templateSettings.category?.price_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text340')}
                  type="price"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category.price_in_filter.order}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
                />
                <FilterSeparator />
              </>
            )}

            {!!templateSettings.category?.suppliers_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text718')}
                  type="generic"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
                  order={templateSettings.category.suppliers_in_filter.order}
                />
                <FilterSeparator />
              </>
            )}
            {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
              <>
                <AttributeSide
                  name={t('text728')}
                  type="supplierLocation"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                  order={templateSettings.category?.suppliers_in_filter?.order}
                />
                <FilterSeparator />
              </>
            )}
          </div>

          <div
            className={cn(
              'grid w-full flex-shrink-0 grid-cols-2 gap-2 px-6 pb-4 pt-4',
              'ea-category-filter-sheet__actions',
            )}
          >
            <SheetClose asChild>
              <Button className="h-[52px] sm:h-14 lg:h-[60px]">{t('text174')}</Button>
            </SheetClose>
            <Button
              variant="outline"
              onClick={clearFilter}
              className="h-[52px] sm:h-14 lg:h-[60px]"
            >
              {t('text632')}
            </Button>
          </div>
        </Card>
      </SheetContent>
    </Sheet>
  );
}
